import { memo } from 'react';
import { toaster } from '@optiaxiom/react';
import { IconEnvelope } from '@optimizely/axiom-icons';
import { NodeProps } from '@xyflow/react';

import { extractApiErrorMessage } from '@frontend/api/http';

import {
  createEmailTrigger,
  deleteEmailTrigger,
  getEmailTrigger,
  updateEmailTrigger,
} from '../../services/email-trigger';
import { NodeConnectionTypes, NodeTypeCategory } from '../../types/connections';
import { NODE_TYPE } from '../../types/node';
import { defineNode } from '../../utils/node-factory';
import { BaseNodeRenderer } from '../components/renderers/BaseNodeRenderer/BaseNodeRenderer';

/**
 * Configuration for the Email Trigger node type.
 * Triggers workflow execution from incoming emails.
 */
export const emailTriggerConfig = defineNode({
  description: {
    displayName: 'Email Trigger',
    name: NODE_TYPE.EMAIL,
    description: 'Trigger workflow execution from incoming emails',
    iconInfo: {
      icon: IconEnvelope,
      color: 'fg.success',
    },
    category: NodeTypeCategory.TRIGGER,
    input: {
      type: NodeConnectionTypes.Main,
      filter: undefined,
    },
    output: {
      type: NodeConnectionTypes.Main,
      displayName: 'Output',
      maxConnections: 1,
    },
    properties: [
      {
        displayName: 'Name',
        name: 'name',
        type: 'string',
        required: true,
        default: '',
        description: 'A descriptive name for this email trigger',
      },
      {
        displayName: 'Description',
        name: 'description',
        type: 'string',
        required: false,
        default: '',
        description: 'Optional description of what emails trigger this workflow',
      },
      {
        displayName: 'Recipient',
        name: 'recipient',
        type: 'string',
        required: true,
        default: '',
        description:
          'Recipient identifier for the email address (kebab-case, e.g., "support-tickets")',
      },
      {
        displayName: 'Email Address',
        name: 'email_address',
        type: 'string',
        required: false,
        default: '',
        description: 'The actual email address for this trigger (display only)',
        readonly: true,
      },
      {
        displayName: 'Subject Filters',
        name: 'subject_filters',
        type: 'filters',
        required: false,
        default: [],
        description: 'Filter emails by subject line. Emails matching any filter will be accepted.',
      },
      {
        displayName: 'Sender Filters',
        name: 'sender_filters',
        type: 'filters',
        required: true,
        default: [],
        description:
          'Filter emails by sender address. Emails matching any filter will be accepted. At least one sender filter is required.',
      },
    ],
    defaults: {
      name: 'Email Trigger',
      parameters: {
        name: '',
        description: '',
        recipient: '',
        email_address: '',
        subject_filters: [],
        sender_filters: [],
      },
    },
  },

  enableAsyncGet: (_nodeId: string, nodeData?: Record<string, unknown>) => {
    const links =
      nodeData && typeof nodeData === 'object' ? (nodeData['links'] as Record<string, string>) : {};
    return Boolean(links && links['self'] && links['self'].trim().length > 0);
  },

  onAsyncSave: async (
    nodeId: string,
    data: Record<string, unknown>,
    context?: { instanceId?: string; workflowAgentId?: string; nodeData?: Record<string, unknown> }
  ) => {
    if (!context?.instanceId || !context?.workflowAgentId) {
      throw new Error('Instance ID or Workflow Agent ID not provided in context');
    }

    console.warn('context.nodeData', context.nodeData);
    if (!context.nodeData || !('links' in context.nodeData)) {
      try {
        const emailTrigger = await createEmailTrigger(
          context.workflowAgentId,
          context.instanceId,
          data
        );
        return emailTrigger;
      } catch (error) {
        const errorMessage = extractApiErrorMessage(error);
        throw new Error(errorMessage);
      }
    }

    const url = (context.nodeData['links'] as Record<string, string>)?.self;
    if (!url) {
      throw new Error('URL not found in node data');
    }

    try {
      await updateEmailTrigger(url, data);
    } catch (error) {
      const errorMessage = extractApiErrorMessage(error);
      throw new Error(errorMessage);
    }
  },

  onAsyncGet: async (
    _nodeId: string,
    context?: { instanceId?: string; workflowAgentId?: string; nodeData?: Record<string, unknown> }
  ) => {
    if (!context?.instanceId || !context?.workflowAgentId) {
      throw new Error('Instance ID or Workflow Agent ID not provided in context');
    }

    if (!context.nodeData || !('links' in context.nodeData)) {
      throw new Error('Node data not found in the context');
    }

    const url = (context.nodeData['links'] as Record<string, string>)?.self;
    if (!url) {
      throw new Error('URL not found in node data');
    }

    try {
      const emailTrigger = await getEmailTrigger(url);
      return emailTrigger;
    } catch (error) {
      if (error && typeof error === 'object' && 'status' in error && error.status === 404) {
        return {};
      }

      console.error('Failed to fetch email trigger:', error);
      toaster.create('Failed to load email trigger configuration', { intent: 'danger' });
      throw error;
    }
  },

  onAsyncDelete: async (
    _nodeId: string,
    context?: { instanceId?: string; workflowAgentId?: string; nodeData?: Record<string, unknown> }
  ) => {
    if (!context?.instanceId || !context?.workflowAgentId) {
      throw new Error('Instance ID or Workflow Agent ID not provided in context');
    }

    if (!context.nodeData || !('links' in context.nodeData)) {
      throw new Error('Node data not found in the context');
    }

    const url = (context.nodeData['links'] as Record<string, string>)?.self;
    if (!url) {
      throw new Error('URL not found in node data');
    }

    try {
      await deleteEmailTrigger(url);
    } catch (error) {
      console.error('Failed to delete email trigger:', error);
      const errorMessage = extractApiErrorMessage(error);
      throw new Error(errorMessage);
    }
  },

  render: memo(function EmailTriggerRenderer(props: NodeProps) {
    return <BaseNodeRenderer {...props} />;
  }),
});
